"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { GemIcon } from "@/components/ui/icons/GemIcon";
import { DataPackagePrice, RootPackagePrice } from "@/types/PackagePriceType";
import { formatCurrency } from "@/utils/formatCurrency";
import { QuestionMarkCircleIcon } from "@heroicons/react/16/solid";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { slideVariants, staggerContainerVariants, staggerItemVariants } from "@/utils/animation";

const BadgeCheckIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-[1.35rem] h-[1.35rem] text-[#2A2A2A] shrink-0 mt-[2px]"
    >
        <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

export default function PackagePrice() {
    const router = useRouter();
    const { t, locale } = useLocale();
    const [packages, setPackages] = useState<DataPackagePrice[]>([]);
    const [isLoadingPackages, setIsLoadingPackages] = useState(true);

    const carouselRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const localePrefix = locale === "en" ? "/en" : "";

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const response = await apiClient.get<RootPackagePrice>('/api/list/paket');
                const data = response.data?.data || [];
                setPackages(data);
            } catch (error) {
                console.error("Gagal memuat daftar paket:", error);
            } finally {
                setIsLoadingPackages(false);
            }
        };

        fetchPackages();
    }, []);

    const checkScrollPosition = useCallback(() => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 20);
            setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 20);
        }
    }, []);

    useEffect(() => {
        if (!isLoadingPackages) {
            const timeoutId = setTimeout(checkScrollPosition, 100);
            window.addEventListener('resize', checkScrollPosition);
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [isLoadingPackages, checkScrollPosition]);

    const scroll = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const scrollAmount = 400;
            carouselRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(checkScrollPosition, 300);
        }
    };

    const handleSelectPackage = (packageId: number) => {
        // Simpan paket ID terpilih ke sessionStorage agar bisa di-checkout setelah step-three
        sessionStorage.setItem('preSelectedPackage', packageId.toString());
        router.push(`${localePrefix}/add-event`);
    };

    return (
        <TooltipProvider delayDuration={150}>
            <section className="bg-[#fdfdfd] py-16 md:py-24 font-sans">
                <div className="max-w-7xl mx-auto px-5 md:px-8 xl:px-0">
                    <motion.div
                        variants={slideVariants(false, 0.6)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="text-center mb-12 md:mb-16"
                    >
                        <h2 className="text-[2rem] md:text-[2.6rem] font-bold text-[#2A2A2A] mb-3 tracking-tight">
                            {t("LandingPage.package_price_title") || "Pilih Paket Sesuai Kebutuhan Acaramu"}
                        </h2>
                        <p className="text-[1.05rem] md:text-[1.15rem] text-[#2A2A2A]">
                            {t("LandingPage.package_price_desc") || "Mulai dari paket Gratis, upgrade kapan saja sesuai kebutuhan"}
                        </p>
                    </motion.div>

                    <div className="relative group max-w-[1200px] mx-auto">
                        <button
                            onClick={() => scroll('left')}
                            disabled={!canScrollLeft}
                            className={`flex md:hidden absolute -left-2 md:-left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full border shadow-md items-center justify-center transition-all duration-300
                                ${canScrollLeft
                                    ? "bg-white border-gray-200 text-gray-600 hover:text-[#E85C57] hover:border-[#E85C57] hover:shadow-lg cursor-pointer"
                                    : "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                                }`}
                            aria-label="Scroll Left"
                        >
                            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                        </button>

                        <motion.div
                            ref={carouselRef}
                            onScroll={checkScrollPosition}
                            variants={staggerContainerVariants(0.06)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.1 }}
                            className="flex gap-6 lg:gap-8 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-10 pt-4 relative z-10 px-2"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {isLoadingPackages ? (
                                [1, 2, 3].map((n) => (
                                    <div key={n} className="min-w-[320px] md:min-w-[360px] flex-1 bg-white rounded-[28px] border border-gray-200 border-t-[10px] border-t-gray-200 p-6 lg:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] animate-pulse h-[650px] snap-center">
                                        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4 mt-2"></div>
                                        <div className="h-5 bg-gray-100 rounded w-full mb-8"></div>
                                        <div className="h-10 bg-gray-200 rounded w-1/2 mb-10"></div>
                                        <div className="space-y-5">
                                            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-5 bg-gray-100 rounded w-4/5"></div>)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                packages.map((pkg) => (
                                    <motion.div
                                        key={pkg.id}
                                        variants={staggerItemVariants(0.5)}
                                        whileHover={{ y: -6 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                        className="relative min-w-[320px] md:min-w-[360px] flex-1 bg-white rounded-[28px] border border-gray-200 border-t-[10px] border-t-[#E85C57] shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_15px_40px_rgba(220,105,101,0.15)] flex flex-col snap-center transition-shadow duration-300"
                                    >
                                        <div className="p-6 lg:p-8 flex flex-col flex-1 relative z-20">
                                            <div className="flex items-center gap-3 mb-2 mt-1">
                                                <GemIcon packageName={pkg.package_name} className="w-7 h-7 flex-shrink-0" />
                                                <h3 className="text-[1.75rem] font-bold text-[#2A2A2A] leading-none">
                                                    {pkg.package_name}
                                                </h3>
                                            </div>

                                            <p className="text-[1rem] text-[#2A2A2A] leading-relaxed mb-4 min-h-[48px]">
                                                {pkg.desc}
                                            </p>

                                            <div className="mb-8">
                                                <div className="text-[2.5rem] font-bold text-[#2A2A2A]">
                                                    {pkg.price === 0 ? "Rp. 0" : formatCurrency(pkg.price)}
                                                </div>
                                            </div>

                                            <ul className="flex flex-col space-y-4 mb-10 flex-1">
                                                {pkg.fitur?.map((item) => (
                                                    <li key={item.id} className="flex items-start gap-3">
                                                        <BadgeCheckIcon />
                                                        <span className="text-[1.05rem] text-[#2A2A2A] font-medium leading-snug">
                                                            {item.fitur_detail?.nama_fitur}
                                                        </span>
                                                        {item.fitur_detail.desc_fitur &&
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        className="shrink-0 mt-[3px] text-gray-400 hover:text-gray-600 transition-colors"
                                                                        aria-label={item.fitur_detail?.nama_fitur ? `Info ${item.fitur_detail.nama_fitur}` : "Info fitur"}
                                                                    >
                                                                        <QuestionMarkCircleIcon className="w-4 h-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>{item.fitur_detail?.desc_fitur}</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        }
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className="flex flex-col gap-3 mt-auto">
                                                <button
                                                    onClick={() => handleSelectPackage(pkg.id)}
                                                    className="w-full py-3.5 px-4 rounded-[14px] bg-[#E85C57] hover:bg-[#C95B57] text-white font-semibold text-[1.05rem] transition-colors shadow-sm"
                                                >
                                                    {t("LandingPage.select_package_btn") || "Pilih Paket"}
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </motion.div>

                        <button
                            onClick={() => scroll('right')}
                            disabled={!canScrollRight}
                            className={`flex md:hidden absolute -right-2 md:-right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full border shadow-md items-center justify-center transition-all duration-300
                                ${canScrollRight
                                    ? "bg-white border-gray-200 text-gray-600 hover:text-[#E85C57] hover:border-[#E85C57] hover:shadow-lg cursor-pointer"
                                    : "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                                }`}
                            aria-label="Scroll Right"
                        >
                            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                    </div>
                </div>
            </section>
        </TooltipProvider>
    );
}